/*
 * Funnel tracking — instrumentation for the sales page.
 *
 * Answers the four questions that actually change what you do next:
 *   1. Which section does she stop scrolling at?
 *   2. Does she reach the label frame in the carousel?
 *   3. Which bundle tier does she land on?
 *   4. Which FAQ does she open first? (that's your real #1 objection)
 *
 * Events go to window.dataLayer and Shopify analytics. They are deliberately
 * NOT sent to Meta: the pixel should carry commerce events only (ViewContent,
 * AddToCart, Purchase), which Shopify emits itself. Pushing scroll-depth and
 * FAQ diagnostics into the pixel bloats the custom-event list and gets names
 * auto-flagged as possibly sensitive, which is exactly what happened here.
 *
 * Everything is feature-detected — if a destination isn't loaded, the event is
 * simply dropped rather than throwing.
 *
 * Judge every change on earnings per view, not conversion rate. A bundle tweak
 * that lowers CVR while raising AOV usually wins on net, and CVR alone will talk
 * you out of it.
 */
(function () {
  'use strict';

  if (window.__avoriFunnelTracking) return;
  window.__avoriFunnelTracking = true;

  var fired = Object.create(null);

  function emit(name, payload) {
    var data = payload || {};
    // `name` may be an internal de-duplication key; a payload-supplied `event`
    // always wins so the name that reaches analytics stays stable and readable.
    var eventName = data.event || name;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({}, data, { event: eventName }));

    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      try {
        window.Shopify.analytics.publish(eventName, data);
      } catch (e) {
        /* analytics not ready — drop it */
      }
    }
  }

  // Fire a given event name at most once per page view.
  function emitOnce(name, payload) {
    if (fired[name]) return;
    fired[name] = true;
    emit(name, payload);
  }

  /* ---------- 1. Section reach depth ---------- */
  // Reports the furthest section reached, so you can see exactly where the page
  // loses her rather than guessing from an aggregate scroll percentage.
  function trackSectionDepth() {
    var sections = Array.prototype.slice.call(document.querySelectorAll('[id^="shopify-section-"]'));
    if (!sections.length || !('IntersectionObserver' in window)) return;

    var deepest = -1;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var index = sections.indexOf(entry.target);
          if (index <= deepest) return;
          deepest = index;

          // Shopify ids look like "template--22967482777856__comparison";
          // keep only the readable part.
          var id = entry.target.id.replace('shopify-section-', '');
          var name = id.indexOf('__') > -1 ? id.split('__').pop() : id;

          emitOnce('section_reached:' + name, {
            event: 'avori_section_reached',
            section: name,
            section_index: index,
            section_total: sections.length
          });
        });
      },
      { threshold: 0.4 }
    );

    sections.forEach(function (section) {
      observer.observe(section);
    });
  }

  /* ---------- 2. Carousel swipe-through ---------- */
  // If she isn't reaching the label frame, the carousel order is wrong — that
  // frame handles the single most common objection and needs to be seen.
  function trackCarousel() {
    var gallery = document.querySelector('.product__media-list, media-gallery');
    if (!gallery) return;

    var slides = gallery.querySelectorAll('.product__media-item, slider-component li');
    if (slides.length < 2 || !('IntersectionObserver' in window)) return;

    var seen = 0;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var index = Array.prototype.indexOf.call(slides, entry.target) + 1;
          if (index <= seen) return;
          seen = index;

          emitOnce('carousel_frame:' + index, {
            event: 'avori_carousel_frame',
            frame: index,
            frames_total: slides.length
          });
        });
      },
      { threshold: 0.6 }
    );

    Array.prototype.forEach.call(slides, function (slide) {
      observer.observe(slide);
    });
  }

  /* ---------- 3. Bundle tier selection ---------- */
  function trackBundleTier() {
    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.closest) return;

      var card = input.closest('.bundle-selector__tier, [class*="bundle"][class*="tier"]');
      if (!card) return;

      var labelEl = card.querySelector('[class*="name"], [class*="title"]');
      emit('avori_bundle_tier_selected', {
        tier: labelEl ? labelEl.textContent.trim() : (input.value || 'unknown')
      });
    });
  }

  /* ---------- 4. FAQ opens ---------- */
  // The most-opened question is your real #1 objection. If it isn't near the top
  // of the page already, move it there.
  function trackFaq() {
    var rows = document.querySelectorAll('.collapsible-content details, .accordion details, details.accordion');
    if (!rows.length) return;

    var firstOpened = false;

    Array.prototype.forEach.call(rows, function (row) {
      row.addEventListener('toggle', function () {
        if (!row.open) return;

        var summary = row.querySelector('summary');
        var question = summary ? summary.textContent.trim().slice(0, 90) : 'unknown';

        emit('avori_faq_opened', { question: question });

        if (!firstOpened) {
          firstOpened = true;
          emit('avori_faq_first_opened', { question: question });
        }
      });
    });
  }

  /* ---------- 5. Guarantee engagement ---------- */
  // Clicks through to the terms mean the risk reversal is doing work.
  function trackGuarantee() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('a[href*="100-nights"]');
      if (!link) return;
      emit('avori_guarantee_terms_clicked', { href: link.getAttribute('href') });
    });
  }

  /* ---------- 6. Outbound source citations ---------- */
  // Clicks on the FTC links mean she is checking your receipts — which is the
  // highest-intent signal on the page, not a leak to be plugged.
  function trackCitations() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('.receipts__source');
      if (!link) return;
      emit('avori_citation_clicked', { href: link.getAttribute('href') });
    });
  }

  function init() {
    trackSectionDepth();
    trackCarousel();
    trackBundleTier();
    trackFaq();
    trackGuarantee();
    trackCitations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
